import type { ReactNode } from 'react';

interface PanelProps {
  title: string;
  /** One line under the title saying what the panel answers. */
  description?: ReactNode;
  /** Right-hand slot: a status pill, a count, a window switcher. */
  sub?: ReactNode;
  actions?: ReactNode;
  /** Skip the body padding when the children are full-bleed rows or a table. */
  flush?: boolean;
  children: ReactNode;
}

/**
 * The card every section sits in: header band with the title, a description of
 * what it answers, and a right-hand slot for state.
 */
export function Panel({ title, description, sub, actions, flush, children }: PanelProps) {
  return (
    <section className="panel">
      <PanelHead title={title} description={description} sub={sub} actions={actions} />
      {flush ? children : <div className="panel__body">{children}</div>}
    </section>
  );
}

export function PanelHead({
  title,
  description,
  sub,
  actions,
}: {
  title: string;
  description?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="panel__head">
      <div style={{ minWidth: 0 }}>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {sub ? <span className="panel__sub">{sub}</span> : null}
      {actions ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 'none' }}>{actions}</div>
      ) : null}
    </header>
  );
}

/** A pill whose tone carries the panel's state — the only colour in a header. */
export function StatusPill({ tone, children }: { tone: 'bad' | 'warn' | 'good' | 'neutral'; children: ReactNode }) {
  return <span className={`status-pill ${tone === 'neutral' ? '' : `status-pill--${tone}`}`}>{children}</span>;
}

/**
 * Names an engine that lands in a later sprint.
 *
 * Deliberately empty of numbers: a placeholder that draws a plausible-looking
 * chart teaches the reader to trust a shape that no engine produced.
 */
export function PendingPanel({
  title,
  description,
  sprint,
  detail,
}: {
  title: string;
  description: string;
  sprint: string;
  detail: ReactNode;
}) {
  return (
    <Panel title={title} description={description} sub={<span className="status-pill">{sprint}</span>} flush>
      <div className="placeholder-note">{detail}</div>
    </Panel>
  );
}
